import { useMemo } from 'react';
import { useCurrentApp } from '@/stores/userDataStore';
import { SourceMediaAsset, PlayAppMediaAssetStatus } from '@/types/media';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { getSocialMediaAssets, MediaType, SocialMediaAsset, SocialMediaResponse } from '@/api/social-media';
import { useQuery } from '@tanstack/react-query';
import { useAppsByAppKey } from '@/api/queries/useAppsByAppKey';
const PAGE_SIZE = 30;
const TWO_MINUTES_IN_MILLIS = 120000;

export type PlayAppMediaAsset = SourceMediaAsset & {
  id: string;
  source?: string;
  status?: PlayAppMediaAssetStatus;
  name?: string;
  createdAt?: string;
};

export const providerToSourceMap = {
  [MediaAssetProviders.playApp]: undefined,
  [MediaAssetProviders.instagram]: MediaAssetProviders.instagram,
  [MediaAssetProviders.tiktok]: MediaAssetProviders.tiktok,
};

export type SocialMediaPreviewQueryProps = {
  provider: MediaAssetProviders;
  type?: MediaType.image | MediaType.video;
};

export const useSocialMediaPreviewQuery = (props: SocialMediaPreviewQueryProps) => {
  const { provider, type } = props;

  const { appKey, appUrl, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();
  const { data: apps = [], isLoading: isAppsLoading } = useAppsByAppKey(appKey);
  const source = providerToSourceMap[provider];
  const queryVariables = {
    appKey: appKey,
    appUrl: appUrl,
    source,
  };

  const { data, isLoading } = useQuery<SocialMediaResponse>({
    queryKey: ['social-media-preview', type, queryVariables],
    queryFn: async () => {
      const response = await getSocialMediaAssets(
        {
          appKey,
          from: 0,
          size: PAGE_SIZE,
          type,
          source,
        },
        apps
      );

      return response;
    },
    enabled: !isAppsLoading && isLoggedIn,
    staleTime: TWO_MINUTES_IN_MILLIS,
  });

  const assets: SocialMediaAsset[] = useMemo(() => {
    if (!data) {
      return [];
    }

    const socialMediaAssets: SocialMediaAsset[] = data.items;
    return socialMediaAssets.map(
      (asset) =>
        ({
          ...asset,
          source: provider,
        }) satisfies SocialMediaAsset
    );
  }, [data, provider]);

  return {
    assets,
    isLoading: isLoadingUser || isLoading,
  };
};
